import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { HardHat, Loader2, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const token = sp.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Le mot de passe doit contenir au moins 6 caractères"); return; }
    if (password !== confirm) { setError("Les deux mots de passe ne correspondent pas"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      toast.success(data.message || "Mot de passe réinitialisé");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 dark:bg-slate-950">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500">
            <HardHat className="h-6 w-6 text-slate-900" />
          </div>
          <span className="font-head text-xl font-extrabold">IR INGENIERIE</span>
        </div>

        {!token ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 dark:border-red-900 dark:bg-red-950/40" data-testid="reset-no-token">
            <h2 className="font-head text-2xl font-bold text-slate-900 dark:text-white">Lien invalide</h2>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">Ce lien de réinitialisation est incomplet ou invalide. Veuillez refaire une demande.</p>
            <Link to="/forgot-password">
              <Button className="mt-5 h-11 w-full bg-slate-900 text-white dark:bg-white dark:text-slate-900">Refaire une demande</Button>
            </Link>
          </div>
        ) : (
          <>
            <h2 className="font-head text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Nouveau mot de passe</h2>
            <p className="mt-2 text-sm text-slate-500">Choisissez un nouveau mot de passe pour votre compte.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-xs font-semibold text-slate-500">Nouveau mot de passe</span>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-password" placeholder="••••••••" name="new-password" autoComplete="new-password"
                    className="h-12 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-900" />
                </div>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-xs font-semibold text-slate-500">Confirmer le mot de passe</span>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-confirm" placeholder="••••••••" name="confirm-password" autoComplete="new-password"
                    className="h-12 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-900" />
                </div>
              </label>
              {error && (
                <div data-testid="reset-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">{error}</div>
              )}
              <Button type="submit" disabled={loading} data-testid="reset-submit"
                className="h-12 w-full bg-amber-500 text-base font-semibold text-slate-900 hover:bg-amber-400">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Réinitialiser le mot de passe"}
              </Button>
              <Link to="/login" className="flex items-center justify-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">
                <ArrowLeft className="h-4 w-4" /> Retour à la connexion
              </Link>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
